import { dayjs } from './dayjs';

/**
 * Escapes special HTML characters to prevent XSS and malformed DOM injection.
 */
export function escapeHtml(str: string | null | undefined): string {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Formats agentDecision into a vibrant, high-contrast status chip.
 */
export function agentDecisionTransformation(value: any): string {
  if (!value || value === 'null') {
    return '<span class="text-xs text-slate-400 font-mono italic">—</span>';
  }

  const normalized = String(value).toUpperCase().trim();

  if (normalized === 'APPROVABLE') {
    return `
      <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-300 shadow-sm" title="Agent is confident this can be approved">
        <svg class="w-3.5 h-3.5 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        <span>Approvable</span>
      </span>
    `.trim();
  }

  if (normalized === 'UNSURE') {
    return `
      <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-300 shadow-sm" title="Agent is unsure whether this can be approved">
        <svg class="w-3.5 h-3.5 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>Unsure</span>
      </span>
    `.trim();
  }

  if (normalized === 'NOT_APPROVABLE') {
    return `
      <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-800 border border-rose-300 shadow-sm" title="Agent thinks this can NOT be approved">
        <svg class="w-3.5 h-3.5 text-rose-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        <span>Not Approvable</span>
      </span>
    `.trim();
  }

  return `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">${escapeHtml(normalized)}</span>`;
}

export function cleanFindingsText(str: string | null | undefined): string {
  if (!str) return '';
  let text = String(str).trim();
  
  if (text.includes('&lt;') || text.includes('&gt;') || text.includes('&quot;') || text.includes('&#039;') || text.includes('&amp;')) {
    text = text
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&amp;/g, '&');
  }

  if (text.includes('<')) {
    text = text.replace(/<[^>]*>/g, '').trim();
  }

  return text;
}

/**
 * Formats agentFindings into an interactive preview chip with a rich hover popover and click trigger.
 */
export function agentFindingsTransformation(value: any, row?: any): string {
  if (!value || value === 'null' || !String(value).trim()) {
    return '<span class="text-xs text-slate-400 font-mono italic">—</span>';
  }

  const rawText = cleanFindingsText(value);
  if (!rawText) {
    return '<span class="text-xs text-slate-400 font-mono italic">—</span>';
  }
  const escapedFull = escapeHtml(rawText);

  // Generate a clean snippet (first non-empty line or first 36 characters)
  const firstLine = rawText.split('\n').map((l) => l.trim()).filter(Boolean)[0] || rawText;
  const snippet = firstLine.length > 36 ? firstLine.substring(0, 34) + '…' : firstLine;
  const escapedSnippet = escapeHtml(snippet);

  const rowId = row?.id ? String(row.id) : Math.random().toString(36).substring(2, 8);
  const anchorName = `--af-anchor-${rowId}`;

  // Decision indicator for popover header
  const decision = row?.agentDecision ? String(row.agentDecision).toUpperCase().trim() : null;
  let decisionBadge = '';
  if (decision === 'APPROVABLE') {
    decisionBadge = '<span class="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-900/80 text-emerald-300 border border-emerald-700">Approvable</span>';
  } else if (decision === 'UNSURE') {
    decisionBadge = '<span class="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-900/80 text-amber-300 border border-amber-700">Unsure</span>';
  } else if (decision === 'NOT_APPROVABLE') {
    decisionBadge = '<span class="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-rose-900/80 text-rose-300 border border-rose-700">Not Approvable</span>';
  }

  return `
    <div class="agent-findings-container relative inline-block group" style="--anchor-id: ${anchorName};">
      <button type="button" 
        class="agent-findings-chip flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-slate-50 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 border border-slate-200 hover:border-indigo-300 shadow-xs transition-all duration-150 cursor-pointer"
        data-id="${escapeHtml(String(row?.id || ''))}"
        data-decision="${escapeHtml(decision || '')}"
        data-findings="${encodeURIComponent(rawText)}"
        title="Click to view full AI assessment or hover for preview">
        <svg class="w-3.5 h-3.5 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
        </svg>
        <span class="truncate max-w-[150px] font-mono text-[11px] text-left">${escapedSnippet}</span>
        <svg class="w-3 h-3 text-slate-400 shrink-0 opacity-70 group-hover:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      <div class="agent-findings-popover pointer-events-none group-hover:pointer-events-auto opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out z-[99999] absolute left-0 top-full mt-1.5 w-80 sm:w-96 rounded-lg bg-slate-900 text-white p-3.5 shadow-2xl border border-slate-700 backdrop-blur-md">
        <div class="flex items-center justify-between border-b border-slate-700 pb-2 mb-2">
          <div class="flex items-center gap-1.5 text-xs font-semibold text-indigo-300">
            <svg class="w-3.5 h-3.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
            <span>Gragent AI Findings</span>
          </div>
          ${decisionBadge}
        </div>
        <div class="font-mono text-xs text-slate-200 whitespace-pre-wrap max-h-60 overflow-y-auto leading-relaxed scrollbar-thin select-text pr-1">
          ${escapedFull}
        </div>
      </div>
    </div>
  `.trim();
}

/**
 * Standard date transformation formatted for Berlin timezone.
 */
export function dateTransformation(value: any): string {
  if (!value || !dayjs(value).isValid()) return '';
  return dayjs(value)
    .tz('Europe/Berlin')
    .locale('de-DE')
    .format('DD.MM.YYYY - HH:mm');
}

/**
 * Standard approvedDate transformation formatted for Berlin timezone.
 */
export function approvedDateTransformation(value: any): string {
  if (!value || !dayjs(value).isValid()) return '';
  return dayjs(value)
    .tz('Europe/Berlin')
    .locale('de-DE')
    .format('DD.MM.YYYY - HH:mm');
}

/**
 * Formats criterionXStatus into a status badge.
 */
export function criterionStatusTransformation(value: any): string {
  if (!value || value === 'null') {
    return '<span class="text-xs text-slate-400 font-mono italic">—</span>';
  }

  const normalized = String(value).toUpperCase().trim();

  if (normalized === 'PASSED') {
    return `
      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-300">
        PASSED
      </span>
    `.trim();
  }

  if (normalized === 'FAILED') {
    return `
      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-800 border border-rose-300">
        FAILED
      </span>
    `.trim();
  }

  if (normalized === 'UNSURE') {
    return `
      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-300">
        UNSURE
      </span>
    `.trim();
  }

  return `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">${escapeHtml(normalized)}</span>`;
}

/**
 * Standard field-transformations array to include in any Purchasepaybacks schema.
 */
export function getStandardFieldTransformations() {
  const transformations = [
    {
      name: 'invoiceNumber',
      transformation: (value: any) => {
        if (!value || value === 'null') {
          return '<span class="text-xs text-slate-400 font-mono italic">—</span>';
        }
        return `<span class="font-mono text-xs font-semibold text-slate-800">${escapeHtml(value)}</span>`;
      },
    },
    {
      name: 'date',
      transformation: dateTransformation,
    },
    {
      name: 'approvedDate',
      transformation: approvedDateTransformation,
    },
    {
      name: 'agentDecision',
      transformation: agentDecisionTransformation,
    },
    {
      name: 'agentFindings',
      transformation: agentFindingsTransformation,
    },
  ];

  for (let i = 1; i <= 12; i++) {
    transformations.push({
      name: `criterion${i}Status`,
      transformation: criterionStatusTransformation,
    });
  }

  return transformations;
}

/**
 * Natural order of active evaluated criteria fields (status and description) for mbd-mbos (1 to 8).
 */
export const CRITERIA_ACTIVE_FIELDS = [
  'criterion1Status',
  'criterion1Description',
  'criterion2Status',
  'criterion2Description',
  'criterion3Status',
  'criterion3Description',
  'criterion4Status',
  'criterion4Description',
  'criterion5Status',
  'criterion5Description',
  'criterion6Status',
  'criterion6Description',
  'criterion7Status',
  'criterion7Description',
  'criterion8Status',
  'criterion8Description',
];

/**
 * Natural order of evaluated criteria fields for field-order in admin tables.
 */
export const CRITERIA_STATUS_FIELDS = CRITERIA_ACTIVE_FIELDS;

/**
 * Unused criteria 9-12 (both status and description) that must be hidden from admin tables.
 */
export const CRITERIA_HIDDEN_FIELDS = [
  'criterion9Status',
  'criterion9Description',
  'criterion10Status',
  'criterion10Description',
  'criterion11Status',
  'criterion11Description',
  'criterion12Status',
  'criterion12Description',
];

/**
 * All 24 criteria columns (1-12 status and descriptions) for readonly or hiding.
 */
export const ALL_CRITERIA_FIELDS = [
  'criterion1Status',
  'criterion1Description',
  'criterion2Status',
  'criterion2Description',
  'criterion3Status',
  'criterion3Description',
  'criterion4Status',
  'criterion4Description',
  'criterion5Status',
  'criterion5Description',
  'criterion6Status',
  'criterion6Description',
  'criterion7Status',
  'criterion7Description',
  'criterion8Status',
  'criterion8Description',
  'criterion9Status',
  'criterion9Description',
  'criterion10Status',
  'criterion10Description',
  'criterion11Status',
  'criterion11Description',
  'criterion12Status',
  'criterion12Description',
];

export const CRITERIA_DROPDOWN_FIELDS = [
  {
    name: 'criterion1Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion2Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion3Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion4Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion5Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion6Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion7Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion8Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion9Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion10Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion11Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
  {
    name: 'criterion12Status',
    default: {
      label: '',
      value: '',
    },
    options: [
      {
        label: '',
        value: '',
      },
      {
        label: 'PASSED',
        value: 'PASSED',
      },
      {
        label: 'FAILED',
        value: 'FAILED',
      },
      {
        label: 'UNSURE',
        value: 'UNSURE',
      },
    ],
  },
];


